import React, { useEffect, useState } from 'react';

import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { useCall } from '../../contexts/CallContext';
import { useWhatsAppCallPermission } from '../../hooks/useWhatsAppCallPermission';
import voiceService, { CallPermission, CallPermissionStatus } from '../../services/voiceService';
import { getApiError } from '../../utils/apiError';

/**
 * "Call patient" — pick phone or WhatsApp, then place the call.
 *
 * Phone dials both the patient and the therapist's own number into a Twilio
 * conference. WhatsApp puts the therapist on this tab and rings the patient's
 * WhatsApp; that route needs the patient's prior approval, so the stored
 * permission is shown before the call rather than after it fails.
 */

type CallMethod = 'phone' | 'whatsapp';

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
  patientPhone: string | null;
  /** Joins the call to this meeting's conference instead of the patient's. */
  meetingId?: number;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const PERMISSION_TEXT: Record<CallPermissionStatus, string> = {
  accepted: 'Patient approved WhatsApp calls',
  requested: "Permission request already sent — waiting for the patient's approval",
  rejected: 'Patient rejected WhatsApp calls',
  expired: 'Call approval expired (older than 7 days)',
  unknown: "Patient hasn't approved WhatsApp calls yet",
};

const CallPatientDialog: React.FC<Props> = ({
  open,
  onClose,
  patientId,
  patientName,
  patientPhone,
  meetingId,
  onSuccess,
  onError,
}) => {
  const [callMethod, setCallMethod] = useState<CallMethod>('phone');
  const [methodTouched, setMethodTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestState, setRequestState] = useState<'idle' | 'sending' | 'sent' | 'already_sent' | 'failed'>('idle');
  const { startWhatsAppCall } = useCall();
  const { permission, setPermission, approved } = useWhatsAppCallPermission({
    patientId,
    phone: patientPhone,
    enabled: open,
  });

  // Until the therapist picks a method themselves, follow the patient's stored
  // consent: an approved patient is best reached on WhatsApp, and defaulting to it
  // saves noticing the badge and switching every time. Guarded on `permission` so
  // an in-flight lookup does not flip the radio to phone and back.
  useEffect(() => {
    if (!methodTouched && permission) setCallMethod(approved ? 'whatsapp' : 'phone');
  }, [approved, permission, methodTouched]);

  // A dialog reopened for another patient starts fresh.
  useEffect(() => {
    if (open) {
      setMethodTouched(false);
      setRequestState('idle');
    }
  }, [open, patientId]);

  const handleSendPermissionRequest = async () => {
    setRequestState('sending');
    try {
      const res = await voiceService.requestWhatsAppCallPermission({
        patient_id: patientId,
        phone: patientPhone || undefined,
      });
      const next = (status: CallPermissionStatus, requestedAt: string | null): CallPermission => ({
        status,
        phone: patientPhone,
        updated_at: null,
        valid_until: null,
        requested_at: requestedAt,
      });
      if (res?.already_approved) {
        setPermission(next('accepted', null));
        setRequestState('sent');
      } else if (res?.already_sent) {
        setPermission(next('requested', permission?.requested_at ?? null));
        setRequestState('already_sent');
      } else {
        setPermission(next('requested', new Date().toISOString()));
        setRequestState('sent');
      }
    } catch (error) {
      console.error('[CallPatientDialog] Permission request failed', error);
      setRequestState('failed');
    }
  };

  const handleCall = async () => {
    if (!patientPhone) return;

    if (callMethod === 'whatsapp') {
      void startWhatsAppCall({ patientId, patientName, phone: patientPhone, meetingId });
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await voiceService.callPatient({ patient_id: patientId, meeting_id: meetingId, phone: patientPhone });
      onSuccess?.('Calling — your phone will ring first, then the patient.');
      onClose();
    } catch (error: any) {
      onError?.(getApiError(error, 'The call could not be started'));
    } finally {
      setSubmitting(false);
    }
  };

  const requestButtonLabel =
    requestState === 'sending'
      ? 'Sending…'
      : requestState === 'sent'
      ? 'Permission request sent'
      : requestState === 'already_sent'
      ? "Request already sent — waiting for the patient's reply"
      : requestState === 'failed'
      ? 'Failed — try again'
      : permission?.status === 'requested'
      ? 'Send request again'
      : 'Send call permission request';

  return (
    <ModalWrapper isOpen={open} onClose={onClose} title="Call Patient" isLoading={submitting} width="max-w-sm">
      <div className="space-y-5">
        <p className="text-sm text-[#366A75] font-medium -mt-2">{patientName}</p>

        <div>
          <label className="block text-sm font-medium text-[#366A75] mb-2">Call Method</label>
          <div className="space-y-2">
            {(
              [
                { value: 'phone', label: 'Phone' },
                { value: 'whatsapp', label: 'WhatsApp' },
              ] as { value: CallMethod; label: string }[]
            ).map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-3 cursor-pointer px-4 py-2 rounded-lg bg-[#EBF5F5] hover:bg-[#D0E7E5] transition-colors"
              >
                <input
                  type="radio"
                  name="call-method"
                  value={opt.value}
                  checked={callMethod === opt.value}
                  onChange={() => {
                    setMethodTouched(true);
                    setCallMethod(opt.value);
                  }}
                  className="accent-[#366A75]"
                />
                <span className="text-sm text-[#366A75] font-medium">{opt.label}</span>
                {/* The badge sits on the WhatsApp row rather than in the hint below,
                    so it stays visible while Phone is selected — that is the thing
                    worth knowing before choosing. */}
                {opt.value === 'whatsapp' && approved && (
                  <span className="ml-auto text-xs font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                    ✓ Approved
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        {callMethod === 'whatsapp' && patientPhone && permission && (
          <div className={`rounded-lg px-3 py-2 text-xs ${approved ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            <p className="font-medium">
              {approved && <span className="mr-1">✓</span>}
              {PERMISSION_TEXT[permission.status]}
              {approved && permission.valid_until && (
                <span className="font-normal opacity-70"> (until {new Date(permission.valid_until).toLocaleDateString()})</span>
              )}
              {permission.status === 'requested' && permission.requested_at && (
                <span className="font-normal opacity-70"> (sent {new Date(permission.requested_at).toLocaleString()})</span>
              )}
            </p>
            {!approved && (
              <>
                <p className="mt-1 opacity-80">
                  WhatsApp calls only reach the patient after they approve calls from the clinic.
                </p>
                <button
                  type="button"
                  onClick={handleSendPermissionRequest}
                  disabled={requestState === 'sending' || requestState === 'sent' || requestState === 'already_sent'}
                  className="mt-1.5 px-2.5 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                >
                  {requestButtonLabel}
                </button>
              </>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-[#366A75] mb-2">Phone Number</label>
          {patientPhone ? (
            <div className={`${POPUP_STYLES.input} text-sm text-[#366A75]`}>{patientPhone}</div>
          ) : (
            <p className="text-sm text-[#366A75]/60">No phone number available</p>
          )}
        </div>

        {callMethod === 'phone' && (
          <p className="text-xs text-[#366A75]/70">
            Your own phone rings first and joins the conference, then the patient is dialed in.
          </p>
        )}

        <div className="flex gap-3 justify-end pt-1">
          <button onClick={onClose} className={POPUP_STYLES.button} disabled={submitting}>
            Cancel
          </button>
          <button onClick={handleCall} disabled={submitting || !patientPhone} className={POPUP_STYLES.button}>
            {submitting ? 'Calling…' : callMethod === 'whatsapp' ? 'WhatsApp Call' : 'Call'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CallPatientDialog;
